import React, { useState } from 'react';
import './App.css';
import OriginalMAList from './components/OriginalMAList';
import AnalogicalMAList from './components/AnalogicalMAList';
import About from './components/About';
import ExampleMA from './components/ExampleMA';
import Interactivity from './components/Interactivity';
import PersonDetailModal from './components/PersonDetailModal';
import { originalMAs, doesMapping, analogicalMAs, people, prompt } from './data/data';
import { Routes, Route, Link } from 'react-router-dom';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './components/ui/collapsible';
import { Button } from './components/ui/button';
import { Code } from 'lucide-react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { sanitizeBoldOnly } from './components/OriginalMAList';

function App() {
    const [showPrompt, setShowPrompt] = useState(false)
    const [selectedMA, setSelectedMA] = useState(null);
    const [showAnalogical, setShowAnalogical] = useState(false);
    const [selectedPerson, setSelectedPerson] = useState(null);

    const handleMASelect = (ma) => {
        setSelectedMA(ma);
        setShowAnalogical(true);
    };

    const handlePersonClick = (person) => {
        setSelectedPerson(person);
    };

    const handleClosePersonModal = () => {
        setSelectedPerson(null);
    };

    return (
        <div className="app">
            <header className="app-header bg-white shadow-sm">
                <div className="max-w-7xl mx-auto px-2 sm:px-3 lg:px-4">
                    <div className="flex flex-col lg:flex-row justify-between items-center py-1 lg:py-2">
                        <div className="w-full lg:w-auto flex items-center gap-4">
                            <img
                                src={`${process.env.PUBLIC_URL}/logomark-chi2025.svg`}
                                alt="CHI 2025"
                                className="hidden lg:block h-10"
                            />                            <Link to="/" className="block flex-1" onClick={() => setSelectedMA(null)}>
                                {/* Desktop Title */}
                                <h1 className="hidden lg:block text-lg font-bold">
                                    Toward Affective Empathy via Personalized Analogy Generation: A Case Study on Microaggression
                                </h1>
                                {/* Mobile Title */}
                                <div className="lg:hidden">
                                    <h1 className="text-base font-bold text-center">
                                        EmoSync
                                    </h1>
                                </div>
                                <span className="lg:ml-2 mt-0.5 lg:mt-0 px-1.5 py-0.5 text-xs font-semibold bg-pink-100 text-pink-800 rounded-full absolute top-1 right-2 lg:relative lg:top-auto lg:right-auto">
                                    CHI 2025
                                </span>
                            </Link>
                        </div>
                        <nav className="mt-0.5 lg:mt-0">
                            <ul className="flex space-x-4">
                                <li>
                                    <Link to="/" className="text-sm lg:text-base font-medium text-gray-600 hover:text-gray-900" onClick={() => setSelectedMA(null)}>
                                        Home
                                    </Link>
                                </li>
                                <li>
                                    <Link to="/example" className="text-sm lg:text-base font-medium text-gray-600 hover:text-gray-900">
                                        Example
                                    </Link>
                                </li>
                                <li>
                                    <Link to="/interactivity" className="text-sm lg:text-base font-medium text-gray-600 hover:text-gray-900">
                                        Interactivity
                                    </Link>
                                </li>
                                <li>
                                    <Link to="/about" className="text-sm lg:text-base font-medium text-gray-600 hover:text-gray-900">
                                        About
                                    </Link>
                                </li>
                            </ul>
                        </nav>
                    </div>
                </div>
            </header>

            <main className="app-main overflow-hidden">
                <Routes>
                    <Route path="/" element={
                        <div className="ma-container min-h-0 flex flex-col h-full">
                            {/* Mobile View Toggle */}
                            <div className="lg:hidden flex flex-col items-center py-2 flex-shrink-0">
                                <div className="flex w-full max-w-md justify-center space-x-4 py-2 px-4">
                                    <button
                                        onClick={() => {
                                            setShowAnalogical(false)
                                            setSelectedMA(null)
                                        }}
                                        className={`flex-1 px-4 py-3 rounded-lg relative ${!showAnalogical
                                            ? 'bg-primary text-white shadow-lg ring-2 ring-primary ring-offset-2'
                                            : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                                            }`}
                                    >
                                        <span className="absolute -top-2.5 left-2 flex items-center justify-center w-5 h-5 bg-primary text-white text-xs font-bold rounded-full">1</span>
                                        <span className="block text-center">Original MA</span>
                                        <span className="block text-xs mt-0.5 opacity-75">Select a case</span>
                                    </button>
                                    <div className="flex items-center text-gray-300">
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </div>
                                    <button
                                        onClick={() => setShowAnalogical(true)}
                                        disabled={!selectedMA}
                                        className={`flex-1 px-4 py-3 rounded-lg relative ${showAnalogical
                                            ? 'bg-primary text-white shadow-lg ring-2 ring-primary ring-offset-2'
                                            : selectedMA
                                                ? 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                                                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                            }`}
                                    >
                                        <span className="absolute -top-2.5 left-2 flex items-center justify-center w-5 h-5 bg-primary text-white text-xs font-bold rounded-full">2</span>
                                        <span className="block text-center">Analogical MA</span>
                                        <span className="block text-xs mt-0.5 opacity-75">View analogies</span>
                                    </button>
                                </div>
                                {showAnalogical &&
                                    <Collapsible open={showPrompt} onOpenChange={setShowPrompt} className="w-full px-4 mt-2">
                                        <CollapsibleTrigger asChild>
                                            <Button variant="outline" size="sm" className="w-full flex items-center justify-center gap-2">
                                                <Code className="h-4 w-4" />
                                                Show Sample Prompt
                                                {showPrompt ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                            </Button>
                                        </CollapsibleTrigger>
                                        <CollapsibleContent>
                                            <div className="mt-2 rounded-md border bg-muted p-4 max-h-[400px] overflow-y-auto">
                                                <pre className="text-sm font-mono whitespace-pre-wrap break-words">{prompt}</pre>
                                            </div>
                                        </CollapsibleContent>
                                    </Collapsible>
                                }
                            </div>

                            {/* Mobile View */}
                            <div className="block lg:hidden flex-1 min-h-0 overflow-y-auto">
                                <div className={`${!showAnalogical ? 'block' : 'hidden'} h-full`}>
                                    <OriginalMAList
                                        items={originalMAs}
                                        onSelect={handleMASelect}
                                        selectedMA={selectedMA}
                                        people={people}
                                        useOriginalIndex={true}
                                    />
                                </div>
                                <div className={`${showAnalogical ? 'block' : 'hidden'} h-full flex flex-col`}>
                                    {selectedMA ? (
                                        <>
                                            <div className="flex-shrink-0 pt-4">
                                                <div
                                                    className="bg-muted/50 p-6 mb-3 rounded-lg mx-4 ring-2 ring-primary/30 shadow-md cursor-pointer hover:bg-muted/70 transition-colors"
                                                    onClick={() => {
                                                        setSelectedMA(null);
                                                        setShowAnalogical(false);
                                                    }}
                                                >
                                                    <div className="text-base font-medium text-muted-foreground mb-3">Selected Original MA:</div>
                                                    <div className="flex items-start gap-4">
                                                        <span className="font-bold text-2xl text-primary min-w-[2.5rem]">
                                                            {originalMAs.findIndex(item => item.id === selectedMA.id) + 1}.
                                                        </span>
                                                        <p className="text-lg leading-relaxed" dangerouslySetInnerHTML={{ __html: sanitizeBoldOnly(selectedMA.text) }} />
                                                    </div>
                                                    <div className="flex items-center gap-2 mt-2">
                                                        <span className="text-base font-medium text-primary bg-primary/10 px-3 py-1 rounded-full">
                                                            {selectedMA.domain}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="flex justify-center items-center py-1">
                                                    <div className="flex items-center justify-center w-10 h-10 rounded-full bg-muted">
                                                        <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                                                        </svg>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex-1 overflow-y-auto min-h-0">
                                                <div className="pb-20">
                                                    <AnalogicalMAList
                                                        mapping={analogicalMAs[selectedMA.id]}
                                                        people={people}
                                                        onPersonClick={handlePersonClick}
                                                    />
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="select-prompt p-4 text-center">
                                            <p>Select an Original MA to view analogical MAs</p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Desktop View */}
                            <div className="hidden lg:flex flex-row h-full pb-4">
                                <div className={`${selectedMA ? 'w-3/5' : 'w-5/6'} pr-4`}>
                                    <div className="sticky top-0 z-10 bg-white pb-4">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <span className="flex items-center justify-center w-6 h-6 bg-primary text-white text-sm font-bold rounded-full shadow-sm">1</span>
                                                <h2 className="text-xl font-semibold">Original Microaggressions</h2>
                                            </div>
                                            {selectedMA && (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => setSelectedMA(null)}
                                                    className="text-sm"
                                                >
                                                    Reset Selection
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                    {selectedMA ? (
                                        <div className="sticky top-20">
                                            <OriginalMAList
                                                items={[selectedMA]}
                                                onSelect={handleMASelect}
                                                selectedMA={selectedMA}
                                                people={people}
                                                isExpanded={true}
                                                originalIndex={selectedMA ? originalMAs.findIndex(item => item.id === selectedMA.id) + 1 : undefined}
                                            />
                                        </div>
                                    ) : (
                                        <OriginalMAList
                                            items={originalMAs}
                                            onSelect={handleMASelect}
                                            selectedMA={selectedMA}
                                            people={people}
                                            useOriginalIndex={true}
                                        />
                                    )}
                                </div>
                                <div className="flex items-center text-gray-300 px-4">
                                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                </div>
                                <div className="w-2/3 pl-4">
                                    <div className="sticky top-0 z-10 bg-white pb-4">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <span className="flex items-center justify-center w-6 h-6 bg-primary text-white text-sm font-bold rounded-full shadow-sm">2</span>
                                                <h2 className="text-xl font-semibold">Analogical Microaggressions</h2>
                                            </div>
                                            <div className="flex flex-col gap-2">
                                                <span className="text-base text-muted-foreground">View analogies</span>
                                            </div>
                                        </div>

                                        <Collapsible open={showPrompt} onOpenChange={setShowPrompt}>
                                            <CollapsibleTrigger asChild>
                                                <Button variant="outline" size="sm" className="flex items-center gap-2">
                                                    <Code className="h-4 w-4" />
                                                    Show Sample Prompt
                                                    {showPrompt ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                                </Button>
                                            </CollapsibleTrigger>
                                            <CollapsibleContent>
                                                <div className="mt-2 rounded-md border bg-muted p-4 max-h-[400px] overflow-y-auto">
                                                    <pre className="text-sm font-mono whitespace-pre-wrap break-words">{prompt}</pre>
                                                </div>
                                            </CollapsibleContent>
                                        </Collapsible>
                                    </div>

                                    {selectedMA ? (
                                        <AnalogicalMAList
                                            mapping={analogicalMAs[selectedMA.id]}
                                            people={people}
                                            onPersonClick={handlePersonClick}
                                        />
                                    ) : (
                                        <div className="select-prompt p-4 text-center">
                                            <p>Select an Original MA to view analogical MAs</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    } />
                    <Route path="/example" element={
                        <div className="h-full overflow-y-auto">
                            <ExampleMA />
                        </div>
                    } />
                    <Route path="/interactivity" element={
                        <div className="h-full overflow-y-auto">
                            <Interactivity />
                        </div>
                    } />
                    <Route path="/about" element={
                        <div className="h-full overflow-y-auto">
                            <About />
                        </div>
                    } />
                </Routes>
            </main>
            <PersonDetailModal
                person={selectedPerson}
                onClose={handleClosePersonModal}
            />
        </div>
    );
}

export default App; 